import { Children, type ButtonHTMLAttributes, type ReactNode } from 'react'
import styles from './Button.module.css'

/*
  Ровно два стиля кнопки плюс иконка — как в макете. Там на всех
  ~1200 кнопках всех экранов встречаются только два вида: тёмная
  основная и светлая с рамкой, обе высотой 48. Ни «прозрачной» 40px
  без рамки (бывший ghost), ни красной (бывший danger) в макете нет:
  «Да, удалить», «Да, выйти», «Отключить 2FA» нарисованы обычной
  основной кнопкой, а опасность действия несёт заголовок диалога
  и кнопка отказа рядом. Варианты убраны из типа, чтобы не вернулись
  «на пять минут» и снова не разъехались с макетом на 23 диалогах.
*/
type Variant = 'primary' | 'secondary' | 'icon'

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant
  /** Во всю ширину — так кнопки стоят в карточках и формах. */
  block?: boolean
  /** Идёт запрос: кнопка блокируется сама, чтобы не отправить дважды. */
  busy?: boolean
  /** Опасное действие иконкой — красная при наведении. */
  tone?: 'bad'
  children?: ReactNode
}

/**
 * Кнопка из макета: Primary и Secondary, состояния Idle / Hover / Disabled.
 *
 * `busy` блокирует кнопку сам, а не полагается на `disabled` снаружи.
 * Двойное нажатие на «Вывести» — это два перевода, и защищаться от него
 * в каждой форме заново значит однажды забыть.
 */
/*
  Подпись оборачивается в span с отступами 0 4 — так устроен компонент
  кнопки в макете (text-wrapper внутри button). Значки заворачивать
  нельзя: у кнопки-иконки 48x48 лишние 8px по бокам ломают квадрат.
*/
function label(children: ReactNode): ReactNode {
  return Children.map(children, (child) => {
    if (typeof child !== 'string' && typeof child !== 'number') return child
    // Пробел из JSX («<Icon /> Добавить») — настоящий текстовый узел,
    // он добавляет кнопке лишние 4px поверх зазора gap. Срезаем.
    const text = String(child).trim()
    return text ? <span className={styles.label}>{text}</span> : null
  })
}

export function Button({
  variant = 'primary', block, busy, tone, disabled, children, type = 'button', ...rest
}: Props) {
  const classes = [styles.button, styles[variant]]
  if (block) classes.push(styles.block)
  if (tone === 'bad') classes.push(styles.toneBad)

  return (
    <button
      {...rest}
      type={type}
      className={classes.join(' ')}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? <span className={styles.spinner} aria-hidden="true" /> : label(children)}
    </button>
  )
}
